import { z } from '@hono/zod-openapi'

export const LanguageCodeSchema = z.string()
  .min(2).max(10)
  .regex(/^[a-z]{2,3}(-[A-Z][a-z]{3})?(-[A-Z]{2})?$/)
  .openapi({
    example: 'es-US',
    description: 'BCP-47 language tag with optional extended subtags (language-script-region)',
  })

export const ScriptDirectionSchema = z.enum(['ltr', 'rtl']).openapi({ example: 'ltr' })

export const ScriptSchema = z.enum([
  'Latn', 'Cyrl', 'Arab', 'Hani', 'Hang', 'Kana', 'Grek', 'Hebr', 'Deva', 'Thai',
  'Armn', 'Geor', 'Ethi', 'Mong', 'Tibt', 'Mymr', 'Khmr', 'Laoo', 'Sinh', 'Taml',
]).openapi({ example: 'Latn' })

export const LanguageRegistryEntrySchema = z.object({
  code: LanguageCodeSchema,
  name: z.string().min(1).max(100).openapi({ example: 'Spanish (United States)' }),
  nativeName: z.string().min(1).max(100).openapi({ example: 'Español (EE. UU.)' }),
  script: ScriptSchema,
  direction: ScriptDirectionSchema,
  pluralRule: z.string().nullable().openapi({ example: 'one_other', description: 'CLDR plural rules category' }),
  isRtl: z.boolean(),
  territory: z.string().nullable().openapi({ example: 'US' }),
  territoryName: z.string().nullable().openapi({ example: 'United States' }),
  isDefault: z.boolean(),
}).openapi('LanguageRegistryEntry', { description: 'A single entry in the language registry with script, direction, and region metadata' })

export const LanguageRegistrySchema = z.object({
  version: z.number().int().min(1).openapi({ example: 1 }),
  languages: z.array(LanguageRegistryEntrySchema).min(1),
  updatedAt: z.string().datetime(),
}).openapi('LanguageRegistry', { description: 'The complete language registry for the CetusEd platform' })

export const LanguageCode = z.object({
  code: LanguageCodeSchema,
  script: ScriptSchema,
  direction: ScriptDirectionSchema,
}).openapi('LanguageCode', { description: 'A validated BCP-47 language code with script and direction' })

export type LanguageCode = z.infer<typeof LanguageCodeSchema>
export type ScriptDirection = z.infer<typeof ScriptDirectionSchema>
export type Script = z.infer<typeof ScriptSchema>
export type LanguageRegistryEntry = z.infer<typeof LanguageRegistryEntrySchema>
export type LanguageRegistry = z.infer<typeof LanguageRegistrySchema>
